import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import * as React from "react";
import { topScoringInhibitors } from "../../../../settings";
import InhibitorColumnChart from "./InhibitorColumnChart";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResultsContext } from "../../context/results-context";

interface Props {
  onlyAssociatedDrugs: boolean,
}

function InhibitorPaginatedColumnChart({onlyAssociatedDrugs}: Props) {

  // Context:

  const {results, selectedInhibitor, setSelectedInhibitor} = useResultsContext();

  // State:

  const [first, setFirst] = useState(0);

  // Memos:

  const chartInhibitorsAll = useMemo(() => {
    return (results.inhibitors ?? []).filter(inh => inh.inChart);
  }, [results.inhibitors]);

  const chartInhibitorsOfAssociatedDrugs = useMemo(() => {
    return chartInhibitorsAll.filter(inh => inh.associatedDrug);
  }, [chartInhibitorsAll]);

  const inhibitors = onlyAssociatedDrugs ? chartInhibitorsOfAssociatedDrugs : chartInhibitorsAll;

  const currentPage = useMemo(() => {
    return inhibitors.slice(first, first + topScoringInhibitors.perPage);
  }, [inhibitors, first]);

  const minYAxis = useMemo(() => {
    return Math.floor(Math.min(0, ...chartInhibitorsAll.flatMap(inh => [inh.bindingEnergy.wt, inh.bindingEnergy.mut])));
  }, [chartInhibitorsAll]);

  // Refs:
  const inhibitorsRef = useRef(inhibitors);
  useEffect(() => {
    inhibitorsRef.current = inhibitors;
  }, [inhibitors]);

  // Effects:

  useEffect(() => {
    // When inhibitors change, go to the first page.
    setFirst(0);
  }, [inhibitors]);

  useEffect(() => {
    // Change page to show newly selected inhibitor.
    if (selectedInhibitor) {
      const idx = inhibitorsRef.current.indexOf(selectedInhibitor);
      if (idx >= 0) {
        const first = idx - idx % topScoringInhibitors.perPage;
        setFirst(first);
        return;
      }
    }
  }, [selectedInhibitor]);

  // Callbacks:

  const handlePageChange = useCallback((e: PaginatorPageChangeEvent) => {
    setFirst(e.first);
  }, []);

  // Render section:

  // Each time when the inhibitor set changes, chart has to be remounted.
  // To achieve that, we need to provide "key" which is unique for a given inhibitor set.
  // Key is composed of:
  // - 'associated' or 'all', based on the 'show only associated drugs' checkbox
  // - idx of the first inhibitor in the current page
  // - length of the inhibitor array (for the progressive report, new inhibitors may be added, it's safer to remount the chart, although maybe not necessary)
  const chartKey = [
    onlyAssociatedDrugs ? 'associated' : 'all',
    first,
    inhibitors.length,
  ].join('_');

  return <>
    <InhibitorColumnChart
      key={chartKey}
      mutation={results.mutationStr}
      inhibitors={currentPage}
      selectedInhibitor={selectedInhibitor}
      setSelectedInhibitor={setSelectedInhibitor}
      min={minYAxis}
      max={0}
    />
    <Paginator
      totalRecords={inhibitors.length}
      rows={topScoringInhibitors.perPage}
      first={first}
      template="PrevPageLink PageLinks NextPageLink"
      onPageChange={handlePageChange}
    />
  </>
}

export default InhibitorPaginatedColumnChart;
